// Η προκαταβολή πελάτη στην πληρωμή του Take Away (απόφαση χρήστη 14/10/2026): ποιες προκαταβολές
// αφαιρούνται από την παραγγελία που χτυπιέται τώρα και πόσα μένουν να πληρωθούν. Μόνο στη μνήμη του
// ταμείου ώσπου να φύγει η παραγγελία: η αφαίρεση γράφεται στον server ΜΑΖΙ με την παραγγελία
// (depositUses), που ξαναελέγχει διαθέσιμο, πελάτη και σύνολο.
//
//   • Μόνο με ανοιχτό ΚΑΙ έτοιμο διακόπτη (useDepositsConfig) — αλλιώς κανένα κουμπί.
//   • Άδειο καλάθι / ολοκληρωμένη πώληση → η επιλογή φεύγει.
//   • Το σύνολο έπεσε κάτω από την προκαταβολή → η επιλογή φεύγει και ο ταμίας διαλέγει ξανά (ποτέ
//     σιωπηλή μείωση του ποσού που αφαιρείται).

import { useCallback, useEffect, useRef, useState } from 'react';
import { depositErrorKind, depositUsesBody, sumCents, type DepositUse } from './model';
import { invalidateDepositsConfig, useDepositsConfig } from './useDepositsConfig';
import type { CustomerDeposit } from './types';

export interface TakeAwayDepositSelection {
  customer: CustomerDeposit['customer'];
  uses: DepositUse[];
}

export function useTakeAwayDeposit({ storeId, totalCents, active, canAdd, onInvalidated }: {
  storeId: number | null | undefined;
  /** Το σύνολο της παραγγελίας σε λεπτά (με εκπτώσεις και τέλη). */
  totalCents: number;
  /** Υπάρχει παραγγελία στο καλάθι. */
  active: boolean;
  /** Επιτρέπεται νέα επιλογή (π.χ. όχι σε εκπαιδευτική παραγγελία). */
  canAdd: boolean;
  /** Η επιλογή ακυρώθηκε γιατί το σύνολο έπεσε κάτω από την προκαταβολή. */
  onInvalidated?: () => void;
}) {
  const cfg = useDepositsConfig(storeId);
  const enabled = cfg.config?.enabled === true && cfg.config?.ready === true;
  const [selection, setSelection] = useState<TakeAwayDepositSelection | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const invalidated = useRef(onInvalidated);
  invalidated.current = onInvalidated;

  const appliedCents = selection ? sumCents(selection.uses) : 0;
  const dueCents = Math.max(0, totalCents - appliedCents);

  useEffect(() => {
    if (!active) { setSelection(null); setSheetOpen(false); }
  }, [active]);

  useEffect(() => {
    if (selection && appliedCents > totalCents) {
      setSelection(null);
      invalidated.current?.();
    }
  }, [selection, appliedCents, totalCents]);

  // Ο διακόπτης έκλεισε στο μεταξύ (ή ο server δεν είναι έτοιμος): καμία προκαταβολή.
  useEffect(() => {
    if (cfg.loaded && !enabled && selection) setSelection(null);
  }, [cfg.loaded, enabled, selection]);

  const { reload } = cfg;
  /** Άρνηση του server στην παραγγελία: η επιλογή φεύγει όταν δεν ισχύει πια (το μήνυμά του το δείχνει ο καλών). */
  const onServerError = useCallback((code: string | null | undefined) => {
    const kind = depositErrorKind(code);
    if (kind === 'reselect') setSelection(null);
    if (code === 'feature_disabled' || code === 'counter_deposits_not_ready') {
      if (storeId) invalidateDepositsConfig(storeId);
      reload(true);
    }
    return kind;
  }, [storeId, reload]);

  return {
    /** Ο διακόπτης επιτρέπει προκαταβολές στο κατάστημα. */
    enabled,
    /** Το κουμπί «Προκαταβολή πελάτη» / η γραμμή της αφαίρεσης. */
    showControl: enabled && active && (canAdd || selection != null),
    selection,
    appliedCents,
    dueCents,
    /** Η προκαταβολή καλύπτει ΟΛΟ το ποσό: κανένας άλλος τρόπος πληρωμής. */
    covered: selection != null && appliedCents > 0 && dueCents === 0,
    sheetOpen,
    openSheet: () => setSheetOpen(true),
    closeSheet: () => setSheetOpen(false),
    apply: (next: TakeAwayDepositSelection) => { setSelection(next.uses.length ? next : null); setSheetOpen(false); },
    remove: () => { setSelection(null); setSheetOpen(false); },
    /** Τα πεδία της παραγγελίας (POST /orders/:storeId/takeaway). */
    orderFields: (): { depositUses?: Array<{ paymentId: number; amount: string }> } =>
      (selection && selection.uses.length ? { depositUses: depositUsesBody(selection.uses) } : {}),
    onServerError,
  };
}

export type TakeAwayDeposit = ReturnType<typeof useTakeAwayDeposit>;
